import React, { useState, useEffect, useRef } from 'react';
import {
  Grid, Box, Typography, CircularProgress, Button,
  Link, Tooltip, IconButton, Chip
} from '@material-ui/core';
import { QRCodeSVG } from 'qrcode.react';
import FileCopyIcon from '@material-ui/icons/FileCopy';
import fileDownload from 'js-file-download';
import Alert from '@material-ui/lab/Alert';
import { useParams } from 'react-router';
import Notification from '../../utils/notification';
import Widget from '../../components/Widget/Widget';
import logo from '../../images/logo-escuro.png';
import api from '../../services/api';
import { getError } from '../../utils/getResponseError';
import {
  basicDate, basicHour, dateWithHours, parseISO
} from '../../utils/dateFormatter';
import { removeCaracteres, toCurrency } from '../../utils/mask';
import useStyles from './styles';
import { ESTADO_FATURA } from '../../constants/estado-fatura';

import logoDev from '../../images/logo-dev.png';

export default function Boleto() {
  const params = useParams();
  const classes = useStyles();
  const { nossoNumero } = params;
  const [loading, setLoading] = useState(true);
  const [loadingPdf, setLoadingPdf] = useState(false);
  const [fatura, setFatura] = useState(null);
  const [error, setError] = useState(null);
  const pollingRef = useRef(null);

  const getStatusColor = (estadoId) => {
    switch (estadoId) {
      case ESTADO_FATURA.PAGO:
        return 'success';
      case ESTADO_FATURA.PENDENTE:
        return 'warning';
      case ESTADO_FATURA.PROCESSANDO:
        return 'primary';
      case ESTADO_FATURA.VENCIDO:
        return 'secondary';
      default:
        return 'default';
    }
  };

  useEffect(() => {
    faturaRequest();
  }, [nossoNumero]);

  // Cleanup polling on unmount
  useEffect(() => () => stopPolling(), []);

  async function faturaRequest() {
    setLoading(true);
    try {
      const { data } = await api.get(`/fatura/${nossoNumero}/detalhes`);
      setFatura(data);

      // Start polling if fatura is PENDENTE, PROCESSANDO, or VENCIDO
      if (data.id_estado_fatura === ESTADO_FATURA.PENDENTE || data.id_estado_fatura === ESTADO_FATURA.PROCESSANDO || data.id_estado_fatura === ESTADO_FATURA.VENCIDO) {
        startPolling();
      }
    } catch (error) {
      console.error(error);
      setError(getError(error));
    }
    setLoading(false);
  }

  function copiarCodigo() {
    Notification({
      type: 'shipped',
      message: 'Copiado!',
      variant: 'contained',
      color: 'success',
    });
  }

  function startPolling() {
    stopPolling(); // Clear any existing polling

    const interval = setInterval(async () => {
      try {
        const { data } = await api.get(`/fatura/${nossoNumero}/detalhes`);
        setFatura(data);

        // Stop polling if status is no longer PENDENTE, PROCESSANDO, or VENCIDO
        if (data.id_estado_fatura !== ESTADO_FATURA.PENDENTE && data.id_estado_fatura !== ESTADO_FATURA.PROCESSANDO && data.id_estado_fatura !== ESTADO_FATURA.VENCIDO) {
          stopPolling();
        }
      } catch (error) {
        console.error('Error polling fatura:', error);
      }
    }, 5000); // Poll every 5 seconds

    pollingRef.current = interval;
  }

  function stopPolling() {
    if (pollingRef.current) {
      clearInterval(pollingRef.current);
      pollingRef.current = null;
    }
  }

  async function copyToClipboard(text, label) {
    try {
      await navigator.clipboard.writeText(text);
      Notification({
        type: 'shipped',
        message: `${label} copiado para a área de transferência`,
        variant: 'contained',
        color: 'success',
      });
    } catch (error) {
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = text;
      document.body.appendChild(textArea);
      textArea.select();
      try {
        document.execCommand('copy');
        Notification({
          type: 'shipped',
          message: `${label} copiado para a área de transferência`,
          variant: 'contained',
          color: 'success',
        });
      } catch (fallbackError) {
        Notification({
          type: 'report',
          message: 'Erro ao copiar para a área de transferência',
          variant: 'contained',
          color: 'secondary',
        });
      }
      document.body.removeChild(textArea);
    }
  }

  async function handlePdf() {
    setLoadingPdf(true);
    try {
      const { data } = await api.get(`/fatura/${nossoNumero}/download`, {
        responseType: 'blob'
      });
      fileDownload(data, `fatura_${fatura.id_fatura}.pdf`);
      Notification({
        type: 'shipped',
        message: 'PDF gerado',
        variant: 'contained',
        color: 'success',
      });
    } catch (error) {
      Notification({
        type: 'report',
        message: 'Erro ao gerar o boleto',
        variant: 'contained',
        color: 'secondary',
        options: {
          position: 'bottom-right'
        }
      });
    }
    setLoadingPdf(false);
  }

  return (
    <>
      <Box
        mt={5}
        width="100%"
        display="flex"
        alignItems="center"
        justifyContent="center"
        flexDirection="column"
        style={{
          padding: '0 30px'
        }}
      >
        <Box
          mb={5}
          style={{
            padding: '0 30px'
          }}
        >
          <img
            src={logo}
            alt="Logo"
            style={{
              width: '100%',
              maxWidth: '367px'
            }}
          />
        </Box>

        <Widget
          style={{
            width: '100%',
            maxWidth: '1000px',
            padding: '20px',
          }}
          disableWidgetMenu
        >
          {loading && (
            <Box
              width="100%"
              display="flex"
              alignItems="center"
              justifyContent="center"
            >
              <CircularProgress />
            </Box>
          )}
          {!loading && error && (
            <Alert severity="error">{error}</Alert>
          )}
          {!loading && !error && fatura && (
            <Grid container spacing={4}>
              <Grid item xs={12}>
                <Box
                  className={classes.titulo}
                  style={{
                    textAlign: 'center',
                    padding: '20px',
                    border: '1px solid #e0e0e0',
                    borderRadius: '8px',
                    backgroundColor: '#f8f9fa'
                  }}
                >
                  <Box style={{ textAlign: 'left' }}>
                    <Typography variant="h3" color="textSecondary" noWrap style={{ marginBottom: '8px' }}>
                      Fatura #
                      {fatura.id_fatura}
                    </Typography>
                    <Typography variant="h5" color="textSecondary" noWrap style={{ marginBottom: '8px' }}>
                      {toCurrency(fatura.valor, true)}
                    </Typography>
                    <Chip
                      size="small"
                      label={fatura.estado}
                      color={getStatusColor(fatura.id_estado_fatura)}
                    />
                  </Box>
                  <Box style={{ textAlign: 'right' }}>
                    <Typography
                      variant="h6"
                      color="textSecondary"
                    >
                      {fatura.cliente.nome}
                    </Typography>
                    <Typography
                      variant="h6"
                      color="textSecondary"
                    >
                      {fatura.produto.nome}
                    </Typography>
                    <Typography
                      variant="h6"
                      color="textSecondary"
                    >
                      Vencimento em
                      {' '}
                      {basicDate(parseISO(fatura.vencimento))}
                    </Typography>
                  </Box>
                </Box>
              </Grid>

              {/* Payment Confirmation Box - Full Width */}
              {fatura.id_estado_fatura === ESTADO_FATURA.PAGO && (
                <Grid item xs={12}>
                  <Box
                    display="flex"
                    flexDirection="column"
                    alignItems="center"
                    justifyContent="center"
                    p={4}
                    border={1}
                    borderColor="success.light"
                    borderRadius={8}
                    style={{
                      marginBottom: 20,
                      backgroundColor: '#e8f5e8',
                      minHeight: 150
                    }}
                  >
                    <Typography variant="h4" color="success.dark" align="center" gutterBottom>
                      ✅ Pagamento Confirmado
                    </Typography>
                    <Typography variant="h6" color="success.dark" align="center">
                      Esta fatura foi paga com sucesso
                    </Typography>
                    <Typography variant="body1" color="success.dark" align="center" style={{ marginTop: 8 }}>
                      Pago em:
                      {' '}
                      {dateWithHours(parseISO(fatura.atualizado_em))}
                    </Typography>
                  </Box>
                </Grid>
              )}

              {/* Payment Methods Row - Only show for active payment states */}
              {(fatura.id_estado_fatura === ESTADO_FATURA.PENDENTE || fatura.id_estado_fatura === ESTADO_FATURA.PROCESSANDO || fatura.id_estado_fatura === ESTADO_FATURA.VENCIDO) && (
                <>
                  {/* First Row: PIX | Boleto */}
                  <Grid item md={6} xs={12}>
                    {((fatura.id_estado_fatura === ESTADO_FATURA.PENDENTE || fatura.id_estado_fatura === ESTADO_FATURA.PROCESSANDO || fatura.id_estado_fatura === ESTADO_FATURA.VENCIDO) && fatura.pix_copia_e_cola) ? (
                      <Box
                        display="flex"
                        flexDirection="column"
                        alignItems="center"
                        p={3}
                        border={1}
                        borderColor="grey.300"
                        borderRadius={8}
                        style={{ marginBottom: 20, height: '100%' }}
                      >
                        <Typography
                          variant="h5"
                          color="textPrimary"
                          noWrap
                          style={{
                            marginBottom: 20,
                            textAlign: 'center'
                          }}
                        >
                          Pague com PIX
                        </Typography>

                        <Box mb={3}>
                          <QRCodeSVG
                            value={fatura.pix_copia_e_cola}
                            size={180}
                            level="M"
                            includeMargin
                          />
                        </Box>

                        <Typography variant="body1" color="textSecondary" align="center" style={{ marginBottom: 15 }}>
                          Escaneie o QR Code para pagar via PIX
                        </Typography>

                        <Box width="100%" mb={2}>
                          <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                            <Typography variant="subtitle2" color="textSecondary">
                              PIX Copia e Cola:
                            </Typography>
                            <Tooltip title="Copiar código PIX">
                              <IconButton
                                size="small"
                                onClick={() => copyToClipboard(fatura.pix_copia_e_cola, 'Código PIX')}
                              >
                                <FileCopyIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Box>
                          <Typography variant="body2" style={{ wordBreak: 'break-all', fontFamily: 'monospace', fontSize: '0.7rem' }}>
                            {fatura.pix_copia_e_cola}
                          </Typography>
                        </Box>

                        {(fatura.id_estado_fatura === ESTADO_FATURA.PENDENTE || fatura.id_estado_fatura === ESTADO_FATURA.PROCESSANDO || fatura.id_estado_fatura === ESTADO_FATURA.VENCIDO) && (
                          <Box mt={2}>
                            <Chip
                              size="small"
                              label="Aguardando"
                              color="warning"
                              icon={<CircularProgress size={10} />}
                              style={{ padding: '8px 12px' }}
                            />
                          </Box>
                        )}
                      </Box>
                    ) : (
                      <Box
                        display="flex"
                        flexDirection="column"
                        alignItems="center"
                        justifyContent="center"
                        p={4}
                        border={1}
                        borderColor="grey.200"
                        borderRadius={8}
                        style={{
                          marginBottom: 20,
                          height: '100%',
                          minHeight: 300
                        }}
                      >
                        <Typography variant="body1" color="textSecondary" align="center">
                          PIX não disponível
                        </Typography>
                      </Box>
                    )}
                  </Grid>

                  <Grid item md={6} xs={12}>
                    {(fatura.id_estado_fatura === ESTADO_FATURA.PENDENTE || fatura.id_estado_fatura === ESTADO_FATURA.PROCESSANDO || fatura.id_estado_fatura === ESTADO_FATURA.VENCIDO) ? (
                      <Box
                        p={3}
                        border={1}
                        borderColor="grey.200"
                        borderRadius={8}
                        style={{ marginBottom: 20, height: '100%' }}
                      >
                        <Typography
                          variant="h5"
                          color="textPrimary"
                          noWrap
                          style={{
                            marginBottom: 20,
                            textAlign: 'center'
                          }}
                        >
                          Boleto Bancário
                        </Typography>
                        <Typography
                          variant="h6"
                          color="textSecondary"
                          style={{
                            textAlign: 'center',
                            marginBottom: 15
                          }}
                        >
                          Código de barras:
                        </Typography>
                        <Grid container spacing={2}>
                          <Grid
                            item
                            xs={8}
                            style={{
                              overflow: 'auto'
                            }}
                          >
                            <Typography variant="body2" style={{ wordBreak: 'break-all', fontFamily: 'monospace', fontSize: '0.7rem' }}>
                              {fatura.codigo_de_barras && removeCaracteres(fatura.codigo_de_barras)}
                            </Typography>
                          </Grid>
                          <Grid item xs={4}>
                            <Tooltip title="Copiar código de barras">
                              <IconButton
                                size="small"
                                onClick={() => copyToClipboard(fatura.codigo_de_barras ? removeCaracteres(fatura.codigo_de_barras) : '', 'Código de barras')}
                              >
                                <FileCopyIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Grid>
                        </Grid>

                        <Box
                          mt={4}
                          width="100%"
                          display="flex"
                          alignItems="center"
                          justifyContent="center"
                          flexDirection="column"
                        >
                          <Button
                            variant="contained"
                            color="primary"
                            size="large"
                            onClick={() => {
                              if (!loadingPdf) {
                                handlePdf();
                              }
                            }}
                          >
                            {loadingPdf ? (
                              <>
                                <CircularProgress
                                  size={18}
                                  style={{
                                    marginRight: 15
                                  }}
                                  color="inherit"
                                />
                                Carregando
                              </>
                            ) : 'BAIXAR BOLETO'}
                          </Button>
                        </Box>
                      </Box>
                    ) : (
                      <Box
                        display="flex"
                        flexDirection="column"
                        alignItems="center"
                        justifyContent="center"
                        p={4}
                        border={1}
                        borderColor="grey.200"
                        borderRadius={8}
                        style={{ marginBottom: 20, height: '100%', minHeight: 300 }}
                      >
                        <Typography variant="body1" color="textSecondary" align="center">
                          Boleto não disponível
                        </Typography>
                      </Box>
                    )}
                  </Grid>
                </>
              )}

              {/* Notificações */}
              <Grid item md={12} xs={12}>
                <Box
                  p={2}
                  border={1}
                  borderColor="grey.200"
                  borderRadius={8}
                  style={{ marginBottom: 20 }}
                >
                  <Typography
                    variant="h5"
                    color="textPrimary"
                    noWrap
                    style={{
                      marginBottom: 15,
                      textAlign: 'center'
                    }}
                  >
                    Notificações
                  </Typography>
                  {fatura.envios && fatura.envios.length > 0 ? (
                    fatura.envios.map((item) => (
                      <Typography
                        variant="body1"
                        color="textSecondary"
                        key={item.meio}
                        style={{ marginBottom: 8 }}
                      >
                        Enviado em
                        {' '}
                        {basicDate(parseISO(item.criado_em))}
                        {' '}
                        às
                        {' '}
                        {basicHour(parseISO(item.criado_em))}
                        {' '}
                        por
                        {' '}
                        {item.meio}
                      </Typography>
                    ))
                  ) : (
                    <Typography variant="body2" color="textSecondary" style={{ textAlign: 'center' }}>
                      Nenhuma notificação enviada
                    </Typography>
                  )}
                </Box>
              </Grid>

            </Grid>
          )}
        </Widget>
        <Link
          color="primary"
          href="https://wa.me/5583996800612"
          target="_blank"
          className={classes.link}
        >
          <Tooltip
            title="Desenvolvido por Fernando Neto"
            aria-label="dev-fernando"
            placement="left-center"
          >
            <img
              src={logoDev}
              alt="Fernando"
              className={classes.dev}
              aria-label="dev-fernando"
            />
          </Tooltip>
        </Link>
      </Box>

    </>
  );
}
